import { useState } from "react";
import { Link } from "@/lib/router-compat";
import { Send } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import CalendlyButton from "@/components/CalendlyButton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";

const projectTypes = [
  "Aménagement d'intérieur",
  "Décoration",
  "Images 3D",
  "Suivi de travaux",
  "Dossier administratif",
  "Autre",
];

const Contact = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    message: "",
  });
  const [consent, setConsentChecked] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Veuillez remplir tous les champs obligatoires.", variant: "destructive" });
      return;
    }
    if (!consent) {
      toast({ title: "Veuillez accepter la politique de confidentialité.", variant: "destructive" });
      return;
    }
    setLoading(true);

    const { error } = await supabase.from("contact_messages").insert({
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      project_type: form.projectType || null,
      message: form.message.trim(),
    });

    if (error) {
      toast({ title: "Erreur", description: "Impossible d'envoyer le message. Réessayez.", variant: "destructive" });
    } else {
      toast({ title: "Message envoyé !", description: "Je vous répondrai dans les plus brefs délais." });
      analytics.contactFormSubmit(form.projectType);
      setForm({ name: "", email: "", phone: "", projectType: "", message: "" });
      setConsentChecked(false);

      // Send email notification (fire-and-forget)
      supabase.functions.invoke("notify-contact", {
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          projectType: form.projectType || null,
          message: form.message.trim(),
        },
      }).catch(console.error);
    }
    setLoading(false);
  };

  return (
    <Layout>
      <SEOHead
        title="Contact JIIA Studio | Architecte d'intérieur Lyon"
        description="Contactez JIIA Studio pour votre projet d'architecture d'intérieur en région Rhône-Alpes. Premier rendez-vous offert."
        canonical="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact - JIIA Studio",
          url: "https://jiia-studio.com/contact",
          mainEntity: {
            "@type": "LocalBusiness",
            name: "JIIA Studio",
            address: { "@type": "PostalAddress", addressLocality: "Grézieu-la-Varenne", addressRegion: "Auvergne-Rhône-Alpes", addressCountry: "FR" },
            areaServed: "Région Rhône-Alpes",
          },
        }}
      />

      <section className="py-24 md:py-32">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">Contact</p>
              <h1 className="font-heading text-4xl md:text-5xl font-light mb-6">
                Parlons de<br />
                <span className="italic">votre espace</span>
              </h1>
              <p className="text-muted-foreground font-body leading-relaxed mb-4">
                Vous avez un projet, une contrainte, une vision ?<br />
                Décrivez-moi votre espace et ce que vous voulez qu'il raconte.<br />Je reviens vers vous sous 48h.
              </p>
              <p className="text-foreground font-body font-medium">
                Premier rendez-vous offert.
              </p>

              <div className="space-y-4 text-sm font-body text-muted-foreground">
                <p>
                  <span className="text-foreground font-medium">Adresse</span><br />
                  Grézieu-la-Varenne, Ouest Lyon
                </p>
                <p>
                   <span className="text-foreground font-medium">Zone d'intervention</span><br />
                   Lyon et agglomération · Région Rhône-Alpes
                 </p>
              </div>

              <div className="mt-8 pt-6 border-t border-border space-y-3">
                <p className="text-xs font-body uppercase tracking-widest text-muted-foreground">En savoir plus</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/prestations" className="text-sm font-body text-foreground hover:text-accent transition-colors underline underline-offset-4">
                    Mes prestations
                  </Link>
                  <Link to="/projets" className="text-sm font-body text-foreground hover:text-accent transition-colors underline underline-offset-4">
                    Mes réalisations
                  </Link>
                  <Link to="/prestations#faq" className="text-sm font-body text-foreground hover:text-accent transition-colors underline underline-offset-4">
                    Questions fréquentes
                  </Link>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-2 block">Nom *</label>
                    <Input id="contact-name" name="name" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="font-body" maxLength={200} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-2 block">Email *</label>
                    <Input id="contact-email" name="email" type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="font-body" maxLength={255} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-phone" className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-2 block">Téléphone</label>
                    <Input id="contact-phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="font-body" />
                  </div>
                  <div>
                    <label htmlFor="contact-project-type" className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-2 block">Type de projet</label>
                    <select id="contact-project-type" name="projectType" value={form.projectType} onChange={(e) => setForm({ ...form, projectType: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-body ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
                      <option value="">Sélectionner...</option>
                      {projectTypes.map((type) => (<option key={type} value={type}>{type}</option>))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-2 block">Message *</label>
                  <Textarea id="contact-message" name="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="font-body min-h-[140px]" maxLength={5000} />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-border text-accent focus:ring-accent"
                  />
                  <span className="text-xs text-muted-foreground font-body leading-relaxed">
                    J'accepte que mes données soient traitées par JIIA Studio pour répondre à ma demande, 
                    conformément à la{" "}
                    <Link to="/politique-de-confidentialite" className="text-accent hover:underline" target="_blank">
                      politique de confidentialité
                    </Link>. *
                  </span>
                </label>

                <Button type="submit" disabled={loading} className="w-full uppercase tracking-widest font-body text-sm h-12">
                  {loading ? "Envoi en cours..." : "Envoyer"} <Send className="h-4 w-4 ml-2" />
                </Button>

                <div className="relative flex items-center gap-4 py-2">
                  <div className="flex-1 border-t border-border" />
                  <span className="text-xs font-body uppercase tracking-widest text-muted-foreground">ou</span>
                  <div className="flex-1 border-t border-border" />
                </div>

                <CalendlyButton />
              </form>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
